import React, { useState, useEffect, useMemo } from 'react';
import { Select, Spin, Alert, Typography, Button, message, Tag, Card, Row, Col, Tooltip } from 'antd';
import { ThunderboltOutlined, LockOutlined } from '@ant-design/icons';

const { Text, Title } = Typography;
const { Option } = Select;

// Single-league-at-a-time lineup setter: pulls the roster, the league's starting
// roster requirements (expanded into individual slots), weekly projected scores,
// global start%, current starter assignments, lock statuses, and matchups live from MFL,
// then lets the user assign a player to every slot (manually or via the auto-optimizer)
// while respecting game locks, and submit to MFL.
const Lineups = () => {
  const DATA_YEAR = 2026;

  const [leagues, setLeagues] = useState([]);
  const [selectedLeagueID, setSelectedLeagueID] = useState(null);
  const [week, setWeek] = useState(1);
  const [lineupData, setLineupData] = useState(null);
  const [slotAssignments, setSlotAssignments] = useState({}); // slotId -> playerID
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    fetch('/api/waivers/myLeagues?userid=1')
      .then((res) => res.json())
      .then((data) => setLeagues(data.response || []))
      .catch((err) => console.error('Failed to load leagues', err));
  }, []);

  const loadLineupData = async (leagueID, dataweek) => {
    if (!leagueID) return;
    setLoading(true);
    setLoadError(null);
    try {
      const response = await fetch('/api/lineups/liveLineupData', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userid: 1, leagueID, datayear: DATA_YEAR, dataweek })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.message || 'Failed to load lineup data');
      setLineupData(data.response);
      setSlotAssignments(data.response.initialSlotAssignments || {});
    } catch (err) {
      setLoadError(err.message || 'Failed to load lineup data');
      setLineupData(null);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLeague = (leagueID) => {
    setSelectedLeagueID(leagueID);
    setLineupData(null);
    if (leagueID) loadLineupData(leagueID, week);
  };

  const handleWeekChange = (value) => {
    setWeek(value);
    if (selectedLeagueID) loadLineupData(selectedLeagueID, value);
  };

  const eligiblePlayers = useMemo(
    () => (lineupData?.players || []).filter((p) => p.rosterStatus === 'ROSTER'),
    [lineupData]
  );
  const expandedSlots = lineupData?.expandedSlots || [];

  const playerMatchesSlot = (player, slot) => {
    if (!slot.eligiblePositions || slot.eligiblePositions.length === 0) return true;
    return slot.eligiblePositions.includes(player.position);
  };

  const assignedPlayerIDs = useMemo(() => new Set(Object.values(slotAssignments).filter(Boolean)), [slotAssignments]);

  const optionsForSlot = (slot) => {
    const currentSelection = slotAssignments[slot.id];
    return eligiblePlayers
      .filter((p) => playerMatchesSlot(p, slot))
      .filter((p) => p.playerID === currentSelection || (!assignedPlayerIDs.has(p.playerID) && !p.isLocked))
      .sort((a, b) => (b.projectedScore || 0) - (a.projectedScore || 0));
  };

  const handleSlotChange = (slotId, playerID) => {
    setSlotAssignments((prev) => ({ ...prev, [slotId]: playerID || undefined }));
  };

  // Fills mandatory slots (up to each position group's minimum) using the
  // highest start% eligible player, then fills the remaining shared flex slots
  // cross-positionally using the highest projected points - capped per group by
  // its own remaining (max - min) capacity so no single position over-fills the flex pool.
  // Respects player locks: locked starters are kept in their assigned slots, and locked
  // bench players are never inserted into the lineup.
  const handleAutoOptimize = () => {
    if (!expandedSlots.length) return;
    const used = new Set();
    const assignments = {};
    const remainingCapacity = { ...(lineupData.groupCapacity || {}) };

    // 1. Preserve all currently assigned starters who are locked
    expandedSlots.forEach((slot) => {
      const currentPlayerID = slotAssignments[slot.id];
      const player = findPlayer(currentPlayerID);
      if (player && player.isLocked) {
        assignments[slot.id] = player.playerID;
        used.add(player.playerID);
        if (!slot.mandatory) {
          remainingCapacity[player.position] = (remainingCapacity[player.position] ?? 0) - 1;
        }
      }
    });

    const pickBest = (slot, metric, restrictByCapacity) => {
      const candidates = eligiblePlayers
        .filter((p) => playerMatchesSlot(p, slot))
        .filter((p) => !used.has(p.playerID))
        .filter((p) => !p.isLocked) // Cannot pick locked bench players
        .filter((p) => !restrictByCapacity || (remainingCapacity[p.position] ?? 0) > 0)
        .sort((a, b) => (b[metric] || 0) - (a[metric] || 0));
      return candidates[0] || null;
    };

    const byScarcity = (a, b) => a.eligiblePositions.length - b.eligiblePositions.length;

    // 2. Fill unfilled mandatory slots
    const unfilledMandatorySlots = expandedSlots
      .filter((s) => s.mandatory && !assignments[s.id])
      .sort(byScarcity);

    unfilledMandatorySlots.forEach((slot) => {
      const best = pickBest(slot, 'startPercentage', false);
      if (best) {
        assignments[slot.id] = best.playerID;
        used.add(best.playerID);
      }
    });

    // 3. Fill unfilled flex slots
    const unfilledFlexSlots = expandedSlots.filter((s) => !s.mandatory && !assignments[s.id]);
    unfilledFlexSlots.forEach((slot) => {
      const best = pickBest(slot, 'projectedScore', true);
      if (best) {
        assignments[slot.id] = best.playerID;
        used.add(best.playerID);
        remainingCapacity[best.position] = (remainingCapacity[best.position] ?? 0) - 1;
      }
    });

    setSlotAssignments(assignments);
    const lockedCount = Object.values(assignments).filter((id) => findPlayer(id)?.isLocked).length;
    if (lockedCount > 0) {
      message.success(`Lineup auto-optimized (${lockedCount} locked starter${lockedCount > 1 ? 's' : ''} preserved)`);
    } else {
      message.success('Lineup auto-optimized: start% filled minimums, projections filled the rest');
    }
  };

  const findPlayer = (playerID) => eligiblePlayers.find((p) => p.playerID === playerID);

  const formatMatchup = (player) => {
    if (!player?.opponent) return '-';
    return player.isHome ? `vs ${player.opponent}` : `@ ${player.opponent}`;
  };

  // MFL's schedule export only exposes a per-team point spread (no game total/O-U),
  // shown from the player's own team's perspective (negative = favored).
  const formatSpread = (player) => {
    if (player?.spread == null) return null;
    return player.spread > 0 ? `+${player.spread}` : `${player.spread}`;
  };

  // Condenses a player's SportsGameOdds prop lines (rushing/receiving/passing
  // yards, receptions, TD probability) into one short string for display.
  const formatPropStats = (player) => {
    const props = player?.props;
    if (!props) return null;
    const parts = [];
    if (props.passing_yards) parts.push(`${props.passing_yards.toFixed(0)} PaYd`);
    if (props.rushing_yards) parts.push(`${props.rushing_yards.toFixed(0)} RuYd`);
    if (props.receiving_yards) parts.push(`${props.receiving_yards.toFixed(0)} ReYd`);
    if (props.receiving_receptions) parts.push(`${props.receiving_receptions.toFixed(1)} Rec`);
    const tds = (props.passing_touchdowns || 0) + (props.rushing_touchdowns || 0) + (props.touchdowns || 0);
    if (tds) parts.push(`${tds.toFixed(2)} TD`);
    return parts.length ? parts.join(', ') : null;
  };

  // Multi-line breakdown of a player's prop lines for a rich hover tooltip.
  const propTooltipContent = (player) => {
    const props = player?.props;
    if (!props) return null;
    const labels = {
      passing_yards: 'Passing Yards',
      passing_touchdowns: 'Passing TDs',
      rushing_yards: 'Rushing Yards',
      rushing_touchdowns: 'Rushing TDs',
      receiving_yards: 'Receiving Yards',
      receiving_receptions: 'Receptions',
      touchdowns: 'Anytime TD'
    };
    return (
      <div>
        {Object.entries(props).map(([statID, value]) => (
          <div key={statID}>{labels[statID] || statID}: {value.toFixed(2)}</div>
        ))}
        {player.propProjectedScore != null && (
          <div style={{ marginTop: 4, fontWeight: 600 }}>Prop-implied: {player.propProjectedScore.toFixed(2)}pt</div>
        )}
      </div>
    );
  };

  const selectedCount = Object.values(slotAssignments).filter(Boolean).length;
  const totalProjectedPoints = Object.values(slotAssignments)
    .filter(Boolean)
    .reduce((sum, playerID) => {
      const p = findPlayer(playerID);
      if (!p) return sum;
      return sum + (p.actualScore != null ? p.actualScore : (p.projectedScore || 0));
    }, 0);

  const handleSubmit = async () => {
    const playerIDs = Object.values(slotAssignments).filter(Boolean);
    if (!selectedLeagueID || playerIDs.length === 0) return;
    setSubmitting(true);
    try {
      const response = await fetch('/api/lineups/submitLineup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leagueID: selectedLeagueID,
          dataweek: week,
          datayear: DATA_YEAR,
          playerIDs
        })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || data.message || 'Failed to submit lineup');
      message.success('Lineup submitted to MFL');
    } catch (err) {
      message.error(err.message || 'Failed to submit lineup');
    } finally {
      setSubmitting(false);
    }
  };

  const benchPlayers = useMemo(
    () => [...eligiblePlayers]
      .filter((p) => !assignedPlayerIDs.has(p.playerID))
      .sort((a, b) => {
        // Place unlocked candidates first so user sees actionable sub candidates at the top
        if (!a.isLocked && b.isLocked) return -1;
        if (a.isLocked && !b.isLocked) return 1;
        return (b.projectedScore || 0) - (a.projectedScore || 0);
      }),
    [eligiblePlayers, assignedPlayerIDs]
  );

  return (
    <div style={{ padding: 12, fontSize: 12, maxWidth: 1400, margin: '0 auto' }}>
      <div style={{ display: 'flex', gap: 8, marginBottom: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <Title level={4} style={{ margin: 0, marginRight: 8 }}>Set Lineup</Title>
        <Select
          placeholder="Select a league"
          style={{ flex: '1 1 180px', minWidth: 140 }}
          onChange={handleSelectLeague}
          value={selectedLeagueID}
        >
          {leagues.map((l) => <Option key={l.leagueID} value={l.leagueID}>{l.leagueName}</Option>)}
        </Select>
        <Select value={week} style={{ flex: '0 1 90px', minWidth: 80 }} onChange={handleWeekChange}>
          {Array.from({ length: 18 }, (_, i) => i + 1).map((w) => <Option key={w} value={w}>Week {w}</Option>)}
        </Select>
        <Button onClick={() => loadLineupData(selectedLeagueID, week)} disabled={!selectedLeagueID}>Refresh</Button>
        <Button
          type="primary"
          icon={<ThunderboltOutlined />}
          onClick={handleAutoOptimize}
          disabled={!lineupData}
        >
          Auto-Optimize
        </Button>
        {lineupData && (
          <>
            <Text strong style={{ marginLeft: 8 }}>
              Slots: {selectedCount}/{lineupData.totalStarterSlots}
            </Text>
            <Text strong>
              Total: {totalProjectedPoints.toFixed(2)}
            </Text>
            <Button
              type="primary"
              loading={submitting}
              disabled={selectedCount === 0}
              onClick={handleSubmit}
              style={{ flex: '1 1 auto' }}
            >
              Submit to MFL
            </Button>
          </>
        )}
      </div>

      {loadError && <Alert type="error" message={loadError} style={{ marginBottom: 12 }} />}
      {loading && <Spin />}

      {lineupData && (
        <>
          <Row gutter={[6, 6]} style={{ marginBottom: 14 }}>
            {expandedSlots.map((slot) => {
              const selectedPlayerID = slotAssignments[slot.id];
              const selectedPlayer = findPlayer(selectedPlayerID);
              const isLocked = Boolean(selectedPlayer?.isLocked);
              return (
                <Col xs={12} sm={8} md={6} lg={4} xl={3} key={slot.id}>
                  <Card
                    size="small"
                    bodyStyle={{ padding: 6 }}
                    headStyle={{ padding: '0 6px', minHeight: 24, fontSize: 12 }}
                    title={
                      <span>
                        {slot.label}
                        {!slot.mandatory && (
                          <Tag color="blue" style={{ marginLeft: 4, fontSize: 10, lineHeight: '14px', padding: '0 4px' }}>
                            Flex
                          </Tag>
                        )}
                        {isLocked && (
                          <Tag color="red" style={{ marginLeft: 4, fontSize: 10, lineHeight: '14px', padding: '0 4px' }}>
                            <LockOutlined /> Locked
                          </Tag>
                        )}
                      </span>
                    }
                    style={{ borderColor: selectedPlayerID ? '#52c41a' : undefined }}
                  >
                    <Select
                      style={{ width: '100%', marginBottom: 4 }}
                      size="small"
                      placeholder="Select"
                      allowClear={!isLocked}
                      disabled={isLocked}
                      value={selectedPlayerID}
                      onChange={(value) => handleSlotChange(slot.id, value)}
                      optionLabelProp="label"
                    >
                      {optionsForSlot(slot).map((p) => (
                        <Option key={p.playerID} value={p.playerID} label={p.playerName}>
                          {p.playerName} ({p.position}) - {p.projectedScore != null ? p.projectedScore.toFixed(1) : '-'}pt / {p.startPercentage != null ? `${p.startPercentage.toFixed(0)}%` : '-'}
                        </Option>
                      ))}
                    </Select>
                    {selectedPlayer && (
                      <div style={{ fontSize: 11, lineHeight: '14px', color: '#666' }}>
                        <div>
                          {formatMatchup(selectedPlayer)}{formatSpread(selectedPlayer) ? ` (${formatSpread(selectedPlayer)})` : ''} | {selectedPlayer.actualScore != null ? <span style={{ color: '#cf1322', fontWeight: 600 }}>Score: {selectedPlayer.actualScore.toFixed(2)}pt</span> : `${selectedPlayer.projectedScore != null ? selectedPlayer.projectedScore.toFixed(1) : '-'}pt`} | {selectedPlayer.startPercentage != null ? `${selectedPlayer.startPercentage.toFixed(0)}%` : '-'}
                        </div>
                        {formatPropStats(selectedPlayer) && (
                          <Tooltip title={propTooltipContent(selectedPlayer)}>
                            <div style={{ cursor: 'help', textDecoration: 'underline dotted' }}>
                              Props: {formatPropStats(selectedPlayer)}{selectedPlayer.propProjectedScore != null ? ` (${selectedPlayer.propProjectedScore.toFixed(1)}pt)` : ''}
                            </div>
                          </Tooltip>
                        )}
                      </div>
                    )}
                  </Card>
                </Col>
              );
            })}
          </Row>

          <div style={{ marginBottom: 10 }}>
            {lineupData.starterSlots.map((slot, i) => (
              <Tag key={i} style={{ fontSize: 11, padding: '1px 6px', marginBottom: 4 }}>
                {slot.name}: {slot.min === slot.max ? slot.min : `${slot.min}-${slot.max}`}
              </Tag>
            ))}
          </div>

          <Title level={5} style={{ margin: '4px 0' }}>Best Bench Candidates to Sub In</Title>
          <div
            style={{
              border: '1px solid #e8e8e8',
              borderRadius: 4,
              overflow: 'hidden'
            }}
          >
            <div
              style={{
                display: 'flex',
                fontWeight: 600,
                fontSize: 11,
                background: '#fafafa',
                padding: '6px 8px',
                borderBottom: '1px solid #e8e8e8'
              }}
            >
              <span style={{ flex: '2 1 120px' }}>Player</span>
              <span style={{ flex: '1 1 60px', textAlign: 'center' }}>Pos</span>
              <span style={{ flex: '1 1 60px', textAlign: 'center' }}>Opp</span>
              <span style={{ flex: '1 1 60px', textAlign: 'center' }}>Spread</span>
              <span style={{ flex: '1 1 70px', textAlign: 'right' }}>Proj</span>
              <span style={{ flex: '1 1 60px', textAlign: 'right' }}>Start%</span>
              <span style={{ flex: '1 1 70px', textAlign: 'right' }}>Prop Proj</span>
            </div>
            {benchPlayers.length === 0 && (
              <div style={{ padding: 10, textAlign: 'center', color: '#999' }}>No bench players available</div>
            )}
            {benchPlayers.map((p, i) => (
              <div
                key={p.playerID}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  fontSize: 12,
                  padding: '6px 8px',
                  background: i % 2 === 0 ? '#fff' : '#fafcff',
                  borderBottom: i === benchPlayers.length - 1 ? 'none' : '1px solid #f0f0f0'
                }}
              >
                <span style={{ flex: '2 1 120px', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {formatPropStats(p) ? (
                    <Tooltip title={propTooltipContent(p)}>
                      <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>{p.playerName}</span>
                    </Tooltip>
                  ) : p.playerName}
                  {p.isLocked && (
                    <Tag color="red" style={{ marginLeft: 4, fontSize: 10, lineHeight: '14px', padding: '0 4px' }}>
                      <LockOutlined /> Locked
                    </Tag>
                  )}
                </span>
                <span style={{ flex: '1 1 60px', textAlign: 'center', color: '#666' }}>{p.position || '-'}</span>
                <span style={{ flex: '1 1 60px', textAlign: 'center', color: '#666' }}>{formatMatchup(p)}</span>
                <span style={{ flex: '1 1 60px', textAlign: 'center', color: '#666' }}>{formatSpread(p) || '-'}</span>
                <span style={{ flex: '1 1 70px', textAlign: 'right' }}>
                  {p.actualScore != null ? (
                    <span style={{ color: '#cf1322', fontWeight: 600 }}>{p.actualScore.toFixed(2)}</span>
                  ) : (
                    p.projectedScore != null ? p.projectedScore.toFixed(2) : '-'
                  )}
                </span>
                <span style={{ flex: '1 1 60px', textAlign: 'right', color: '#666' }}>{p.startPercentage != null ? `${p.startPercentage.toFixed(1)}%` : '-'}</span>
                <span style={{ flex: '1 1 70px', textAlign: 'right', color: '#666' }}>
                  {formatPropStats(p) ? (
                    <Tooltip title={propTooltipContent(p)}>
                      <span style={{ cursor: 'help', textDecoration: 'underline dotted' }}>{p.propProjectedScore != null ? p.propProjectedScore.toFixed(2) : '-'}</span>
                    </Tooltip>
                  ) : (p.propProjectedScore != null ? p.propProjectedScore.toFixed(2) : '-')}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Lineups;
